import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Face as FaceIcon,
  NoAccounts as NoAccountsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { StatusChip } from '../../components/common/Chips'
import { employeeApi } from '../../api/employeeApi'
import { faceApi } from '../../api/faceApi'
import { attendanceApi } from '../../api/attendanceApi'
import { useFaceIO } from '../../context/FaceIOContext'
import { useLoading } from '../../hooks'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import toast from 'react-hot-toast'
import { formatDate } from '../../utils/formatters'



const EmployeeDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { faceioInstance } = useFaceIO()
  const { loading, withLoading } = useLoading()
  const [employee, setEmployee] = useState(null)
  const [tabValue, setTabValue] = useState(0)
  const [faceEnrollment, setFaceEnrollment] = useState(null)
  const [performanceData, setPerformanceData] = useState([])
  const [attendanceStats, setAttendanceStats] = useState({
    data: [],
    attendance_percent: 0,
    productivity: 0,
    present_days: 0,
    absent_days: 0,
    late_marks: 0
  })

  const fetchAttendanceStats = async () => {
    try {
      const response = await attendanceApi.getEmployeeAttendanceStats(id)
      if (response.data) {
        setAttendanceStats(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch attendance stats:', error)
    }
  }

  const fetchPerformanceData = async () => {
    try {
      const response = await attendanceApi.getEmployeePerformanceChart(id)
      if (response.data?.data) {
        setPerformanceData(response.data.data)
      }
    } catch (error) {
      console.error('Failed to fetch performance chart data:', error)
    }
  }

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      withLoading(async () => {
        try {
          const response = await employeeApi.getEmployeeDetails(id)
          const data = response.data

          // Map API response to component state
          const mappedEmployee = {
            id: data.employee_id,
            employeeId: data.employee_code,
            firstName: data.first_name,
            lastName: data.last_name,
            fullName: `${data.first_name} ${data.last_name}`,
            email: data.email,
            phone: data.phone,
            department: data.department,
            position: data.designation,
            joinDate: data.join_date,
            salary: data.salary,
            address: data.address,
            emergencyContact: data.emergency_contact,
            emergencyPhone: data.emergency_phone,
            bloodGroup: data.blood_group,
            maritalStatus: data.marital_status,
            status: data.status,
            faceEnrolled: data.faceEnrolled,
            photo: data.photo,
            employeeType: data.employee_type,
            workShift: data.work_shift,
            workHours: data.work_hours,
            workingDays: data.working_days,
          }
          setEmployee(mappedEmployee)
        } catch (error) {
          console.error('Failed to fetch employee details:', error)
          toast.error('Failed to load employee details')
        }
      })
    }

    if (id) {
      fetchEmployeeDetails()
      fetchFaceEnrollment()
      fetchAttendanceStats()
      fetchPerformanceData()
    }
  }, [id])

  const fetchFaceEnrollment = async () => {
    try {
      const response = await faceApi.getFaceId(id)
      setFaceEnrollment(response.data)
    } catch (error) {
      console.error('Failed to fetch face enrollment status:', error)
    }
  }

  const handleRemoveFaceID = async () => {
    if (!faceEnrollment?.facial_id) {
      toast.error('No Face ID found for this employee')
      return
    }

    if (!window.confirm("Are you sure you want to remove this employee's Face ID? They will need to re-enroll for biometric attendance.")) {
      return
    }

    withLoading(async () => {
      try {
        // Call backend API to remove enrollment (Handles both FaceIO and Database)
        await faceApi.removeEnrollment(faceEnrollment.facial_id, { skipSuccessToast: true })

        toast.success('Face ID removed successfully')

        // Update local state
        setFaceEnrollment({ ...faceEnrollment, is_enrolled: false, facial_id: null })
        setEmployee((prev) => ({ ...prev, faceEnrolled: false }))
      } catch (error) {
        console.error('Failed to remove Face ID:', error)
      }
    })
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  if (loading && !employee) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Skeleton variant="text" width="60%" height={60} />
              <Skeleton variant="text" width="40%" height={30} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    )
  }

  if (!employee) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Employee not found.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/org/employees')} sx={{ mt: 2 }}>
          Back to Employees
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 3 }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {employee.fullName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Employee ID: {employee.employeeId} • {employee.department}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/org/employees/${employee.id}/edit`)}
                size="small"
                sx={{ py: { xs: 0.5, sm: 1 } }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                startIcon={<FaceIcon />}
                onClick={() => navigate(`/org/employees/${id}/enroll`)}
                disabled={employee.faceEnrolled}
                size="small"
                sx={{ py: { xs: 0.5, sm: 1 } }}
              >
                {employee.faceEnrolled ? 'Face Enrolled' : 'Enroll Face'}
              </Button>
              {employee.faceEnrolled && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<NoAccountsIcon />}
                  onClick={handleRemoveFaceID}
                  size="small"
                  sx={{ py: { xs: 0.5, sm: 1 } }}
                >
                  Remove Face ID
                </Button>
              )}
            </Box>
          </Box>


          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size="auto">
                <StatusChip status={employee.status} size="medium" />
              </Grid>
              <Grid size="auto">
                <Chip
                  label={employee.faceEnrolled ? 'Face Enrolled' : 'Face Not Enrolled'}
                  color={employee.faceEnrolled ? 'success' : 'error'}
                  variant="outlined"
                />
              </Grid>
              <Grid size="auto">
                <Typography variant="body2" color="textSecondary">
                  Joined: {formatDate(employee.joinDate)}
                </Typography>
              </Grid>
              <Grid size="grow" />
            </Grid>
          </Paper>
        </Box>


        <Paper elevation={0} sx={{ mb: 3, border: 'none' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Attendance" />
            <Tab label="Performance" />
            <Tab label="Documents" />
          </Tabs>
        </Paper>


        {tabValue === 0 && (
          <Grid container spacing={3}>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FaceIcon color="primary" /> Personal Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={employee.photo}
                    sx={{ width: 100, height: 100, mr: 3, border: '2px solid #e0e0e0' }}
                  >
                    {employee.firstName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{employee.fullName}</Typography>
                    <Typography variant="body2" color="textSecondary">{employee.position}</Typography>
                  </Box>
                </Box>

                <List dense>
                  <ListItem>
                    <ListItemIcon><EmailIcon color="action" /></ListItemIcon>
                    <ListItemText primary="Email" secondary={employee.email} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><PhoneIcon color="action" /></ListItemIcon>
                    <ListItemText primary="Phone" secondary={employee.phone} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LocationIcon color="action" /></ListItemIcon>
                    <ListItemText primary="Address" secondary={employee.address} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><PeopleIcon color="action" /></ListItemIcon>
                    <ListItemText primary="Marital Status" secondary={employee.maritalStatus || 'N/A'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><WarningIcon color="action" /></ListItemIcon>
                    <ListItemText primary="Blood Group" secondary={employee.bloodGroup || 'N/A'} />
                  </ListItem>
                </List>
              </Paper>
            </Grid>


            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkIcon color="primary" /> Job Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Employee ID" secondary={employee.employeeId} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Department" secondary={employee.department} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Designation" secondary={employee.position} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Employee Type" secondary={employee.employeeType} />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Date of Joining" secondary={formatDate(employee.joinDate)} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Work Shift" secondary={employee.workShift} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Work Hours" secondary={`${employee.workHours} Hours`} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Salary" secondary={`₹${employee.salary?.toLocaleString()}`} />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>


            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="error" /> Emergency Contact
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="textSecondary">Contact Name</Typography>
                    <Typography variant="body1">{employee.emergencyContact}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="textSecondary">Contact Phone</Typography>
                    <Typography variant="body1">{employee.emergencyPhone}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}


        {tabValue === 1 && (
          <Grid container spacing={3}>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AccessTimeIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {attendanceStats.attendance_percent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Attendance</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {attendanceStats.productivity}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Productivity</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {attendanceStats.present_days}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Present Days</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <WarningIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {attendanceStats.late_marks}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Late Marks</Typography>
                </CardContent>
              </Card>
            </Grid>


            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Recent Attendance (Last 7 Days)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Check In</TableCell>
                        <TableCell>Check Out</TableCell>
                        <TableCell>Working Hours</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendanceStats.data.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{formatDate(record.date)}</TableCell>
                          <TableCell><StatusChip status={record.status} variant="outlined" /></TableCell>
                          <TableCell>{record.check_in || '—'}</TableCell>
                          <TableCell>{record.check_out || '—'}</TableCell>
                          <TableCell>{record.working_hours || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        )}

        {tabValue === 2 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Performance Analytics
            </Typography>
            <Box sx={{ height: 300, mt: 3 }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="attendance" stroke="#1976d2" strokeWidth={2} name="Attendance %" />
                  <Line type="monotone" dataKey="productivity" stroke="#2e7d32" strokeWidth={2} name="Productivity %" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        )}

        {tabValue === 3 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Employee Documents
            </Typography>
            <Typography color="textSecondary">
              Employee documents and files will be managed here.
            </Typography>
          </Paper>
        )}
      </Paper>
    </Box>
  )
}

export default EmployeeDetails